import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import { Accordion, AccordionDetails, AccordionSummary, Typography } from '@mui/material'
import Skeleton from '@mui/material/Skeleton'
import classNames from 'classnames'
import React, { useState } from 'react'
import styles from './Details.module.scss'
import useMatchMedia from '../../../hooks/matchMedia'
import { PANEL_KIND, ProductInterface } from '../../../interfaces/ProductInterface'
import { _at } from '../../../utils/translations'
import FormattedMessage from '../../FormattedMessage/FormattedMessage'
import SizeGuide from '../SizeGuide/SizeGuide'

type Props = {
  product?: ProductInterface
  isNoneSize?: boolean
  sizePanelId?: string
  activePanel: false | PANEL_KIND
  onSizeGuideHide: () => void
  hasValidSizePreset: boolean
}

const Details: React.FC<Props> = (props: Props) => {
  const { product, isNoneSize, onSizeGuideHide, hasValidSizePreset } = props
  const { isDesktop } = useMatchMedia()
  const [activePanel, setActivePanel] = useState<PANEL_KIND | false>(props.activePanel || PANEL_KIND.DESCRIPTION)

  const onPanelChange = (panel: PANEL_KIND, expanded: boolean) => {
    setActivePanel(expanded ? panel : false)
  }
  React.useEffect(() => {
    setActivePanel(props.activePanel || false)
  }, [props.activePanel])

  if (!product) {
    return (
      <Skeleton variant="rectangular" classes={{ root: styles['details__container--skeleton'] }}>
        <div className={styles['details__section']} />
      </Skeleton>
    )
  }

  return (
    <div className={styles['details']}>
      {_at('description', product.description.translations) ? (
        <Accordion
          classes={{ root: styles['panel-box'], expanded: styles['panel-box--expanded'] }}
          elevation={0}
          expanded={activePanel === PANEL_KIND.DESCRIPTION}
          onChange={(event: React.ChangeEvent<{}>, expanded: boolean) => onPanelChange(PANEL_KIND.DESCRIPTION, expanded)}
        >
          <AccordionSummary
            expandIcon={activePanel === PANEL_KIND.DESCRIPTION ? <RemoveIcon /> : <AddIcon />}
            classes={{
              content: styles['panel__content'],
              root: classNames(styles['panel'], { [styles['panel--selected']]: activePanel === PANEL_KIND.DESCRIPTION }),
            }}
          >
            <Typography variant="subtitle1" classes={{ subtitle1: styles['typography__subheading'] }}>
              <FormattedMessage id="Product.Label.PanelDescriptionTitle" defaultMessage="A bit about me" />
            </Typography>
          </AccordionSummary>
          <AccordionDetails classes={{ root: styles['panel-details'] }}>
            <Typography
              classes={{ root: styles['typography__description'] }}
              align="justify"
              dangerouslySetInnerHTML={{ __html: _at('description', product.description.translations) }}
            />
          </AccordionDetails>
        </Accordion>
      ) : null}

      {_at('printDescription', product.translations) ? (
        <Accordion
          classes={{ root: styles['panel-box'], expanded: styles['panel-box--expanded'] }}
          elevation={0}
          expanded={activePanel === PANEL_KIND.STORY_PANEL}
          onChange={(event: React.ChangeEvent<{}>, expanded: boolean) => onPanelChange(PANEL_KIND.STORY_PANEL, expanded)}
        >
          <AccordionSummary
            expandIcon={activePanel === PANEL_KIND.STORY_PANEL ? <RemoveIcon /> : <AddIcon />}
            classes={{
              content: styles['panel__content'],
              root: classNames(styles['panel'], { [styles['panel--selected']]: activePanel === PANEL_KIND.STORY_PANEL }),
            }}
          >
            <Typography variant="subtitle1" classes={{ subtitle1: styles['typography__subheading'] }}>
              <FormattedMessage id="Product.Label.PanelMyStoryTitle" defaultMessage="My beginnings" />
            </Typography>
          </AccordionSummary>
          <AccordionDetails classes={{ root: styles['panel-details'] }}>
            <Typography
              classes={{ root: styles['typography__description'] }}
              align="justify"
              dangerouslySetInnerHTML={{ __html: _at('printDescription', product.translations) }}
            />
          </AccordionDetails>
        </Accordion>
      ) : null}
      {!isDesktop && product?.sizeGuidePreset && hasValidSizePreset && !isNoneSize && props.sizePanelId ? (
        <Accordion
          elevation={0}
          classes={{ root: styles['panel-box'], expanded: styles['panel-box--expanded'] }}
          expanded={activePanel === PANEL_KIND.SIZE_PANEL}
          onChange={(event: React.ChangeEvent<{}>, expanded: boolean) => {
            if (!expanded) {
              onSizeGuideHide()
            }
            onPanelChange(PANEL_KIND.SIZE_PANEL, expanded)
          }}
        >
          <AccordionSummary
            expandIcon={activePanel === PANEL_KIND.SIZE_PANEL ? <RemoveIcon /> : <AddIcon />}
            classes={{
              content: styles['panel__content'],
              root: classNames(styles['panel'], { [styles['panel--selected']]: activePanel === PANEL_KIND.SIZE_PANEL }),
            }}
          >
            <Typography variant="subtitle1" classes={{ subtitle1: styles['typography__subheading'] }}>
              <FormattedMessage
                oldId="product.label.panelSizeTitle"
                id="Product.Label.PanelSizeTitle"
                description="Product.Label.PanelSizeTitle"
                defaultMessage="Let's Size You Up"
              />
            </Typography>
          </AccordionSummary>
          <AccordionDetails classes={{ root: styles['panel-details'] }}>
            <SizeGuide presetId={product.sizeGuidePreset} panelId={props.sizePanelId} />
          </AccordionDetails>
        </Accordion>
      ) : null}
    </div>
  )
}

export default Details
